import type {
  LogicTypeStepWorkspaceReadModel,
  WorkspaceWorkspaceReadModel,
  UserMeReadModel
} from '@r/api/generated/client.schemas.ts';

type WorkspaceStep = LogicTypeStepWorkspaceReadModel;
type Workspace = WorkspaceWorkspaceReadModel;

function getNextStep(
  lastReachedStepIndex: number | null,
  workspaceSteps: WorkspaceStep[] | undefined
): WorkspaceStep | null {
  if (workspaceSteps && lastReachedStepIndex !== null) {
    return workspaceSteps[lastReachedStepIndex + 1];
  }
  if (workspaceSteps) {
    return workspaceSteps[0];
  }

  return null;
}

function getLastReachedStepIndex(
  lastReachedStep: WorkspaceStep | null | undefined,
  workspaceSteps: WorkspaceStep[] | undefined
): number | null {
  let lastReachedStepIndex = null;

  if (workspaceSteps && lastReachedStep?.name && lastReachedStep?.stepPoint) {
    lastReachedStepIndex = workspaceSteps.findIndex(
      (step: WorkspaceStep) =>
        step.name === lastReachedStep.name && step.stepPoint === lastReachedStep.stepPoint
    );
  }

  return lastReachedStepIndex;
}

function checkIfLastStepReached(
  workspaceSteps: WorkspaceStep[] | undefined,
  lastReachedStep: WorkspaceStep | null | undefined
): boolean {
  const lastStep = workspaceSteps?.[workspaceSteps.length - 1];

  return !!(
    lastReachedStep &&
    lastReachedStep.name === lastStep?.name &&
    lastReachedStep.stepPoint === lastStep?.stepPoint
  );
}

function getStepAccomplishments(
  pointsAmount: number,
  workspaceSteps: WorkspaceStep[] | undefined,
  isLastStepReached: boolean,
  lastReachedStep: WorkspaceStep | null | undefined
): number[] {
  const stepAccomplishments: number[] = [];

  function processSegmentAccomplishment(
    currentStepPoint: number,
    nextStepPoint: number,
    totalPoints: number
  ) {
    const segmentAmount = nextStepPoint - currentStepPoint;
    let segmentAccomplishment = (totalPoints * 100) / segmentAmount;

    segmentAccomplishment = segmentAccomplishment > 100 ? 100 : segmentAccomplishment;
    segmentAccomplishment = segmentAccomplishment < 0 ? 0 : segmentAccomplishment;

    stepAccomplishments.push(segmentAccomplishment);

    return totalPoints - segmentAmount;
  }

  if (workspaceSteps) {
    const lastStep = workspaceSteps[workspaceSteps.length - 1];

    let totalPoints = pointsAmount;

    if (isLastStepReached) {
      totalPoints = lastStep.stepPoint;
    } else if (lastReachedStep && pointsAmount < lastReachedStep.stepPoint) {
      totalPoints = lastReachedStep.stepPoint;
    }

    for (let i = 0; i < workspaceSteps.length; i++) {
      const currentStep = workspaceSteps[i];
      const upcomingStep = workspaceSteps[i + 1];

      if (i === 0) {
        totalPoints = processSegmentAccomplishment(0, currentStep.stepPoint, totalPoints);
      }

      if (upcomingStep) {
        const segmentAmount = upcomingStep.stepPoint - currentStep.stepPoint;

        if (segmentAmount > 0) {
          totalPoints = processSegmentAccomplishment(
            currentStep.stepPoint,
            upcomingStep.stepPoint,
            totalPoints
          );
        }
      }
    }
  }

  return stepAccomplishments;
}

export const WorkspaceStepsInfoProcessor = (
  workspace: Workspace,
  userData: UserMeReadModel,
  balance?: number
) => {
  const workspaceSteps: WorkspaceStep[] = workspace?.currentLogicWorkspace?.steps ?? [];

  const lastReachedStepId = userData?.visibleWorkspaces?.[0]?.company?.lastStepReached?.id;
  const lastReachedStep = workspaceSteps.find((step) => step.id === lastReachedStepId);
  const lastReachedStepIndex = getLastReachedStepIndex(lastReachedStep, workspaceSteps);
  const nextStep = getNextStep(lastReachedStepIndex, workspaceSteps);
  const isLastStepReached = checkIfLastStepReached(workspaceSteps, lastReachedStep);

  let stepAccomplishments: number[] = [];

  if (userData) {
    stepAccomplishments = getStepAccomplishments(
      balance || 0,
      workspaceSteps,
      isLastStepReached,
      lastReachedStep
    );
  }

  return {
    workspaceSteps,
    lastReachedStep,
    stepAccomplishments,
    nextStep,
    isLastStepReached
  };
};
